import React, { useContext, useEffect } from "react";
import { TestContext } from "../../context/test_context";
import TestOptions from "../../components/global/TestOptions";
import usePayment from "../../hooks/payment_hook";
import { useNavigate } from "react-router-dom";
import { localizedTimeToTest } from "../../utils/time_calculators";
import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";

export default function Settings(): any {
  const { loading } = React.useContext(TestContext);
  const navigate = useNavigate();
  const {
    hasPaid,
    isCheckingPayment,
    getUserSubscriptionSettings,
    subscriptionSettings,
    isLoadingSubscriptionSettings,
  } = usePayment();

  useEffect(() => {
    if (hasPaid && !isCheckingPayment) {
      getUserSubscriptionSettings();
    }
  }, [hasPaid, isCheckingPayment]);

  return (
    <div className="w-full overflow-y-auto px-6 pt-4">
      <h2 className="text-3xl font-bold mb-4">Settings</h2>
      {loading || isCheckingPayment ? (
        <div className="flex justify-center items-center h-full w-full py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="w-full pt-5 flex flex-col h-full gap-8">
          <div className="rounded-lg border border-border bg-card shadow-sm">
            <h3 className="text-2xl font-semibold m-0 p-4 border-b border-border">
              Membership
            </h3>
            <div className="p-4">
              <div className="flex flex-col items-center justify-center mb-4 w-full">
                {hasPaid ? (
                  <div className="flex flex-col items-center justify-center w-full">
                    {isLoadingSubscriptionSettings ? (
                      <div className="flex justify-center my-2">
                        <Spinner />
                      </div>
                    ) : subscriptionSettings ? (
                      <div className="flex flex-row justify-between items-center w-full">
                        <div>
                          <h5 className="text-xl font-semibold mb-2">Upcoming Renewal:</h5>
                          <p className="text-base mb-6">
                            {localizedTimeToTest(subscriptionSettings.renews_at)}
                          </p>
                          <h5 className="text-xl font-semibold mb-2">Your Plan:</h5>
                          <p className="text-base mb-6">{subscriptionSettings.product_name}</p>
                          <h5 className="text-xl font-semibold mb-2">Status:</h5>
                          <h5 className="text-xl font-semibold text-success mb-6">
                            {subscriptionSettings.status}
                          </h5>
                        </div>
                        <div className="flex flex-col items-center justify-center w-1/2">
                          <div className="flex flex-col items-center justify-center w-[70%] gap-4">
                            <Button
                              className="w-full"
                              onClick={() => {
                                if (subscriptionSettings.customer_portal_url) {
                                  window.open(subscriptionSettings.customer_portal_url, "_blank");
                                }
                              }}
                            >
                              Manage Subscription
                            </Button>
                            <Button
                              className="w-full"
                              variant="outline"
                              onClick={() => {
                                if (subscriptionSettings?.update_payment_method_url) {
                                  window.open(
                                    subscriptionSettings.update_payment_method_url,
                                    "_blank"
                                  );
                                }
                              }}
                            >
                              Update Payment Method
                            </Button>
                            <Button
                              className="w-full"
                              variant="outline"
                              onClick={() => {
                                if (
                                  subscriptionSettings?.customer_portal_update_subscription_url
                                ) {
                                  window.open(
                                    subscriptionSettings.customer_portal_update_subscription_url,
                                    "_blank"
                                  );
                                }
                              }}
                            >
                              Change Plan
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" className="w-full" onClick={getUserSubscriptionSettings}>
                        Something went wrong — click to reload subscription management.
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center justify-center">
                    <div className="w-full mb-3 p-4 rounded-md border border-info/40 bg-info/10 text-info-dark text-sm">
                      You don&apos;t have an active subscription.
                    </div>
                    <h4 className="text-2xl font-semibold mb-4">Free Account</h4>
                    <p className="text-base mb-6">
                      Upgrade to premium to access all features and unlimited tests.
                    </p>
                    <Button className="w-full mb-4" size="lg" onClick={() => navigate("/checkout")}>
                      Upgrade to Premium
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card shadow-sm">
            <h3 className="text-2xl font-semibold m-0 p-4 border-b border-border">
              Default Test Settings
            </h3>
            <div className="p-4">
              <TestOptions />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
